import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import toast from 'react-hot-toast';

/**
 * Handles Android hardware / gesture back button:
 * 1. Protects ongoing bookings (warns user before interrupting an active reservation).
 * 2. Protects ongoing payments (warns user before leaving a payment flow).
 * 3. Navigates back in browser history if possible.
 * 4. Double-tap to exit on main root screen.
 */
export const AndroidBackButtonHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lastBackPressRef = useRef(0);

  useEffect(() => {
    let listenerHandle = null;

    const setupListener = async () => {
      try {
        listenerHandle = await CapApp.addListener('backButton', ({ canGoBack }) => {
          // 1. If an active booking modal is open
          if (window.__REGLOW_BOOKING_MODAL && window.__REGLOW_BOOKING_MODAL.isOpen) {
            if (window.__REGLOW_BOOKING_MODAL.step > 1) {
              const confirmLeave = window.confirm(
                'Une réservation est en cours. Voulez-vous vraiment annuler votre sélection ?'
              );
              if (confirmLeave) {
                window.__REGLOW_BOOKING_MODAL.onClose();
              }
            } else {
              window.__REGLOW_BOOKING_MODAL.onClose();
            }
            return;
          }

          // 2. If inside payment flow
          if (location.pathname.includes('/client/payment')) {
            const confirmPaymentLeave = window.confirm(
              'Un paiement est en cours. Êtes-vous certain de vouloir quitter cette page ?'
            );
            if (confirmPaymentLeave) {
              navigate('/dashboard');
            }
            return;
          }

          // 3. Normal navigation history
          const isRootPage = location.pathname === '/' || location.pathname === '/dashboard';
          if (canGoBack && !isRootPage) {
            navigate(-1);
            return;
          }

          // 4. Double-tap on root screen to exit app safely
          const now = Date.now();
          if (now - lastBackPressRef.current < 2000) {
            CapApp.exitApp();
          } else {
            lastBackPressRef.current = now;
            toast('Appuyez à nouveau pour quitter RE-GLOW', {
              icon: '👋',
              duration: 2000,
            });
          }
        });
      } catch (err) {
        // Safe no-op when running on standard web browser
      }
    };

    setupListener();

    return () => {
      if (listenerHandle && listenerHandle.remove) {
        listenerHandle.remove();
      }
    };
  }, [navigate, location]);

  return null;
};

export default AndroidBackButtonHandler;
